import { TemplateCategory } from "services/types/template-categories.type";
import { TextTemplate } from "services/types/text-templates.type";

export interface TemplateSubmitData {
  template: TextTemplate | null;
  needReplace: boolean;
  composedTemplate: string;
}

/**
 * Resolves the payload submitted from the V2 templates dialog.
 *
 * In Compose Template Mode the composed text is the source of truth — even when
 * no single template is highlighted in the tree. Previously the composed text
 * was dropped whenever `template` was null, so "Insert" silently did nothing on
 * a chart note that already had content (SN-18246, bug B). Here we carry the
 * composed text on `template.text` so the downstream insert hook receives real
 * HTML. Outside compose mode (or with empty composed text) the values pass
 * through unchanged, preserving the normal single-template insert path.
 */
export function resolveSubmitTemplate(
  values: TemplateSubmitData,
  composeTemplateEnabled: boolean
): TemplateSubmitData {
  if (!composeTemplateEnabled || !values.composedTemplate?.trim()) {
    return values;
  }

  const template: TextTemplate = values.template
    ? { ...values.template, text: values.composedTemplate }
    : {
        id: "composed-template",
        name: "Composed Template",
        type: 0,
        owner: 0,
        is_public: false,
        text: values.composedTemplate
      };

  return { ...values, template };
}

export interface TreeNode {
  id: number;
  title: string;
  selector: string;
  text?: string;
  parentSelector?: string;
  parentSelectors: string[];
  parentTitles: string[];
  isCategory?: boolean;
  children: TreeNode[];
}

const formatTemplateCategoryToNode = (
  category: TemplateCategory,
  children: TreeNode[],
  parentSelectors: string[] = []
) =>
  ({
    id: category.id,
    title: category.title,
    selector: `c${category.id}`,
    parentSelector: category.parent?.id ? `c${category.parent?.id}` : null,
    parentSelectors,
    parentTitles: [],
    isCategory: true,
    children
  }) as TreeNode;

const formatTextTemplateToNode = (
  template: TextTemplate,
  parent: string | number | null | undefined,
  categorySelectors: Set<string>
) =>
  ({
    id: template.id,
    title: template.name,
    selector: `t${template.id}`,
    parentTitles: [],
    parentSelectors: [],
    parentSelector:
      parent && categorySelectors.has(`c${parent}`) ? `c${parent}` : "c-1",
    children: []
  }) as TreeNode;

export const getCategoriesByParentId = (
  id: number,
  data: TemplateCategory[],
  parentSelectors: string[]
): TreeNode[] =>
  data
    .filter((category) => category.parent?.id === id)
    .map((category) =>
      formatTemplateCategoryToNode(
        category,
        getCategoriesByParentId(category.id, data, [
          ...parentSelectors,
          `c${category.id}`
        ]),
        [...parentSelectors]
      )
    );

export const getCategoryNodesTree = (
  data: TemplateCategory[],
  parentSelectors: string[]
) => {
  const result: TreeNode[] = data
    .filter((category) => !category.parent)
    .map((category) =>
      formatTemplateCategoryToNode(
        category,
        getCategoriesByParentId(category.id, data, [
          ...parentSelectors,
          `c${category.id}`
        ]),
        parentSelectors
      )
    );

  if (!result.length)
    return data.map((category) => formatTemplateCategoryToNode(category, []));
  return result;
};

export const getCategoryNodesFlat = (data: TreeNode[]) => {
  let result: TreeNode[] = [];

  data.forEach((node) => {
    if (!node.children.length) {
      result.push(node);
    } else {
      result = [...result, node, ...getCategoryNodesFlat(node.children)];
    }
  });

  return result;
};

export const getNodesByParentSelector = (
  selector: string,
  parentTitles: string[],
  parentSelectors: string[],
  data: TreeNode[]
): TreeNode[] => {
  const result = data
    .filter((node) => node.parentSelector === selector)
    .map((node) => ({
      ...node,
      parentTitles: [...parentTitles, ...node.parentTitles],
      parentSelectors: [...parentSelectors, ...node.parentSelectors],
      children: node.parentSelector
        ? getNodesByParentSelector(
            node.selector,
            [...parentTitles, ...node.parentTitles, node.title.toLowerCase()],
            [...parentSelectors, ...node.parentSelectors, node.selector],
            data
          ).filter(
            (node) =>
              !node.isCategory || (node.isCategory && node.children.length)
          )
        : []
    }));

  return result;
};

/**
 * Flat list of template nodes with no category nesting. Used for search results
 * so every server-matched template is visible at the top level instead of being
 * buried under a collapsed category (ENG-1426). The server already filtered the
 * set — including matches on body text — so we render it verbatim.
 */
export const getTemplatesFlatNodes = (templates: TextTemplate[]): TreeNode[] =>
  templates.map((template) => ({
    id: Number(template.id),
    title: template.name,
    selector: `t${template.id}`,
    parentTitles: [],
    parentSelectors: [],
    isCategory: false,
    children: []
  }));

export const getTemplatesNodesTree = (
  categories: TemplateCategory[],
  templates: TextTemplate[],
  search: string
) => {
  const categoriesList = [
    {
      id: -1,
      title: "No Category",
      is_active: true,
      parentTitles: [],
      parentSelectors: [],
      parent: null
    },
    ...categories
  ];

  const categorySelectors = new Set<string>();

  const categoryFlatNodes: TreeNode[] = [];
  categoriesList.forEach((category) => {
    const node = formatTemplateCategoryToNode(category, []);
    categoryFlatNodes.push(node);
    if (!categorySelectors.has(node.selector)) {
      categorySelectors.add(node.selector);
    }
  });

  templates.forEach((template) => {
    if (
      template.category?.id &&
      !categorySelectors.has(`c${template.category?.id}`)
    ) {
      const node = formatTemplateCategoryToNode(template.category, []);
      categoryFlatNodes.push(node);
      categorySelectors.add(node.selector);
    }
  });

  const templateFlatNodes = templates.map((template) =>
    formatTextTemplateToNode(
      template,
      template?.category?.id,
      categorySelectors
    )
  ) as TreeNode[];

  const nodes = [...categoryFlatNodes, ...templateFlatNodes];

  const nodesTree = nodes
    .filter((node) => !node.parentSelector)
    .map((node) => ({
      ...node,
      children: getNodesByParentSelector(
        node.selector,
        [...node.parentTitles, node.title.toLowerCase()],
        [...node.parentSelectors, node.selector],
        nodes
      ).filter(
        (node) => !node.isCategory || (node.isCategory && node.children.length)
      )
    }));

  return getNodesUnderFilter(nodesTree, search.toLocaleLowerCase());
};

const getNodesUnderFilter = (nodesTree: TreeNode[], search: string) => {
  let result: TreeNode[] = [];

  nodesTree.forEach((node) => {
    if (
      node.title.toLowerCase().includes(search) ||
      node.parentTitles.join(" ").includes(search)
    ) {
      if ((node.isCategory && node.children.length) || !node.isCategory) {
        result.push(node);
      }
    } else {
      const subNodes = getNodesUnderFilter(node.children, search);

      if (subNodes.length) {
        result = [
          ...result,
          ...subNodes.filter(
            (node) =>
              (node.isCategory && node.children.length) || !node.isCategory
          )
        ];
      }
    }
  });

  return result;
};
